import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Tooltip, CircularProgress } from '@material-ui/core'
import { AlertTriangle } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

/**
 * SMS registrations across every organization. Without this the only way to
 * find a brewery stuck in carrier review is a Rails console, which means
 * nobody looks until the brewery complains.
 */

interface RegistrationRow {
  id: number
  organizationId: number
  organizationName: string
  slug: string
  tier: string
  status: 'collecting' | 'submitted' | 'in_review' | 'approved' | 'rejected'
  mock: boolean
  rejectionReason: string | null
  legalBusinessName: string | null
  submittedAt: string | null
  approvedAt: string | null
  createdAt: string
  lastSentAt: string | null
  issues: string[]
}

const ISSUE_COPY: Record<string, string> = {
  rejected: 'Twilio rejected this registration — the reason is in the status column',
  mock_registration: 'Mock registration: cannot send, and expires 30 days after creation',
  stuck_in_review: 'Submitted more than 14 days ago and still not resolved — worth chasing',
  dormant: 'Approved but no texts sent in 60 days — the Twilio campaign and number still bill monthly',
}

const STATUS_COPY: Record<RegistrationRow['status'], string> = {
  collecting: 'Filling in details',
  submitted: 'Submitted',
  in_review: 'With carriers',
  approved: 'Approved',
  rejected: 'Rejected',
}

const statusColor = (row: RegistrationRow) => {
  if (row.status === 'rejected') return colors.RED
  if (row.status === 'approved') return row.mock ? colors.ORANGE : colors.GREEN
  if (row.status === 'collecting') return colors.GREY_3
  return colors.YELLOW
}

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—'

const SmsRegistrationsSection: FunctionComponent = () => {
  const styles = useStyles()
  const [rows, setRows] = useState<RegistrationRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get<RegistrationRow[]>('/super_admin/sms_registrations')
        setRows(res.data)
      } catch {
        toast.error('Could not load SMS registrations')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <Box className={styles.section}>
      <h2 className={styles.title}>SMS registrations</h2>
      <p className={styles.subtitle}>
        A2P carrier registrations. Each approved one carries a monthly Twilio
        campaign and number fee whether or not the brewery sends.
      </p>

      {loading && <Box className={styles.loading}><CircularProgress size={20} /></Box>}

      {!loading && rows.length === 0 && (
        <p className={styles.empty}>No organization has started SMS setup yet.</p>
      )}

      {!loading && rows.length > 0 && (
        <Box className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Legal name</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Approved</th>
                <th>Last sent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Box className={styles.nameCell}>
                      {row.issues.length > 0 && (
                        <Tooltip
                          title={row.issues.map((i) => ISSUE_COPY[i] ?? i).join(' · ')}
                        >
                          <span className={styles.warnIcon}><AlertTriangle size={14} /></span>
                        </Tooltip>
                      )}
                      <span>
                        <strong>{row.organizationName}</strong>
                        <span className={styles.slug}>/{row.slug}</span>
                      </span>
                    </Box>
                  </td>
                  <td className={styles.muted}>{row.legalBusinessName || '—'}</td>
                  <td>
                    <span
                      className={styles.statusPill}
                      style={{ backgroundColor: statusColor(row) }}
                    >
                      {STATUS_COPY[row.status]}{row.mock ? ' (mock)' : ''}
                    </span>
                    {row.rejectionReason && (
                      <div className={styles.reason}>{row.rejectionReason}</div>
                    )}
                  </td>
                  <td className={styles.muted}>{formatDate(row.submittedAt)}</td>
                  <td className={styles.muted}>{formatDate(row.approvedAt)}</td>
                  <td className={styles.muted}>{formatDate(row.lastSentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  section: {
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 12,
    padding: SPACING * 2,
    background: colors.WHITE,
  },
  // Plain elements, not Typography: MUI v5's emotion styles override
  // makeStyles, so colours set here would silently not apply.
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: colors.STITCH_BLACK },
  subtitle: { margin: `4px 0 ${SPACING * 2}px`, fontSize: 13, color: colors.GREY_3 },
  empty: { margin: 0, fontSize: 14, color: colors.GREY_3 },
  loading: { display: 'flex', justifyContent: 'center', padding: SPACING * 2 },
  // Wide content scrolls inside its own container rather than pushing the
  // page sideways.
  tableWrap: { overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    '& th': {
      textAlign: 'left',
      padding: `${SPACING}px ${SPACING}px`,
      borderBottom: `1px solid ${colors.GREY_5}`,
      color: colors.GREY_2,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    '& td': {
      padding: `${SPACING}px ${SPACING}px`,
      borderBottom: `1px solid ${colors.GREY_6}`,
      verticalAlign: 'top',
    },
  },
  nameCell: { display: 'flex', alignItems: 'center', gap: 6 },
  warnIcon: { display: 'flex', color: colors.ORANGE },
  slug: { marginLeft: 6, fontSize: 12, color: colors.GREY_3 },
  muted: { color: colors.GREY_2, whiteSpace: 'nowrap' },
  statusPill: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    padding: '2px 7px',
    borderRadius: 999,
    color: colors.WHITE,
    whiteSpace: 'nowrap',
  },
  reason: { marginTop: 4, fontSize: 12, color: colors.DARK_RED, maxWidth: 260 },
})

export default SmsRegistrationsSection
